import { Fragment, useEffect, useState } from 'react';

import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import H from '@zola/zola-ui/src/typography/Headings';

import { CardShelf } from '~/components/common/cards/CardShelf/CardShelf';
import ApiService from '~/util/apiService';
import { StandardLogFn } from '~/util/logger';
import { TrackingProductLocation, trackExpertAdviceClicked } from '~/util/trackingHelper';

import styles from './expertAdvice.module.less';

export const DEFAULT_ARTICLES = [
  'a-guide-to-tipping-wedding-vendors',
  'choose-wedding-band-or-dj',
  'how-much-does-a-wedding-photographer-cost',
  '7-wedding-vendors-you-might-forget-to-tip',
  'how-to-choose-your-wedding-caterer',
  'how-to-choose-your-wedding-photographer',
  'how-to-choose-your-wedding-dj',
  'how-to-book-hotel-rooms-for-your-wedding-guests',
  'how-to-write-a-wedding-rfp',
];

export const LOCATION_SPECIFIC_ARTICLES: Record<string, string[]> = {
  'new-york-ny': ['how-to-get-married-in-NYC'], // slugs are case-sensitive
  'chicago-il': ['how-to-get-married-in-chicago'],
};

interface ExpertAdviceProps {
  title?: string;
  articleSlugs?: string[];
  city?: { slug?: string } | null;
  showReadMore?: boolean;
  noRecomputeToShowAll?: boolean;
}

export type PageCardView = {
  tag: string;
  heroImageset: ImageView[];
  href: string;
  title: string;
  excerpt: string;
};

export interface ImageView {
  url: string;
  descriptor: string;
}

const sortArticles = (articles: PageCardView[], slugs: string[]): PageCardView[] => {
  return slugs
    .map((slug) => articles.filter((article) => article.href.includes(slug))[0])
    .filter((x) => x);
};

const getImageSrc = (images: ImageView[]) => {
  return images ? images[0].url : undefined;
};

const getImageSrcSet = (images: ImageView[]) => {
  return images ? images.map((image) => `${image.url} ${image.descriptor}`).join(', ') : undefined;
};

export const ExpertAdviceContent = ({
  articles,
  title = 'Get Expert Vendor Advice',
  showReadMore = true,
  noRecomputeToShowAll,
}: {
  articles: PageCardView[];
  title?: string;
  showReadMore?: boolean;
  noRecomputeToShowAll?: boolean;
}) => {
  if (articles.length === 0) return null;

  return (
    <Fragment>
      <div className={styles.hed}>
        <H.Title2 presentation="h3">{title}</H.Title2>
        {showReadMore && (
          <LinkV2 arrow href="/expert-advice/topic/planning-101/hiring-vendors">
            Read More
          </LinkV2>
        )}
      </div>
      <CardShelf hoverScaleAdjust={false} noRecomputeToShowAll={noRecomputeToShowAll}>
        {articles.map((article, index) => {
          const src = getImageSrc(article.heroImageset);
          const srcSet = getImageSrcSet(article.heroImageset);
          const slug = article.href.split('/').pop() as string;

          const trackClicked = () =>
            trackExpertAdviceClicked({
              location: TrackingProductLocation.EXPLORE_VENDORS_PLP_EXPERT_ADVICE,
              productId: slug,
              name: article.title,
              position: index,
            });

          return (
            <LinkV2
              noUnderline
              className={styles.cardLink}
              href={article.href}
              onClick={trackClicked}
              key={`card-${index}`}
            >
              <div className={styles.card}>
                <div className={styles.imageContainer}>
                  <img src={src} srcSet={srcSet} sizes="(min-width: 992px) 25vw, 100vw" alt="" />
                </div>
                <div className={styles.body}>
                  <div className={styles.header}>{article.title}</div>
                </div>
              </div>
            </LinkV2>
          );
        })}
      </CardShelf>
    </Fragment>
  );
};

const ExpertAdvice = (props: ExpertAdviceProps) => {
  const {
    title = 'Get Expert Vendor Advice',
    articleSlugs = DEFAULT_ARTICLES,
    city,
    showReadMore = true,
    noRecomputeToShowAll,
  } = props;
  const [articles, setArticles] = useState<PageCardView[]>([]);
  const [modifiedSlugs, setModifiedSlugs] = useState([...articleSlugs]);

  useEffect(() => {
    if (city?.slug && LOCATION_SPECIFIC_ARTICLES[city.slug]) {
      setModifiedSlugs([...LOCATION_SPECIFIC_ARTICLES[city.slug], ...articleSlugs]);
    }
  }, [city, articleSlugs]);

  useEffect(() => {
    ApiService.post<PageCardView[]>(`/web-marketplace-api/v1/content-api/pages/by-slug`, {
      slugs: modifiedSlugs,
    })
      .then((newArticles) => {
        setArticles(sortArticles(newArticles, modifiedSlugs));
      })
      .catch(StandardLogFn);
  }, [modifiedSlugs]);

  return (
    <ExpertAdviceContent
      articles={articles}
      title={title}
      showReadMore={showReadMore}
      noRecomputeToShowAll={noRecomputeToShowAll}
    />
  );
};

export default ExpertAdvice;
